import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { Helmet } from "react-helmet-async";
import config from "../config";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight, FiCheckCircle } from "react-icons/fi";
import AuthHeader from "./AuthHeader";
import AuthFooter from "./AuthFooter";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [forgotemail, setForgotEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.onmouseenter = Swal.stopTimer;
      toast.onmouseleave = Swal.resumeTimer;
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("verified") === "true") {
      Toast.fire({
        icon: "success",
        title: "Email verified, you can login now!",
      });
    }
    // eslint-disable-next-line
  }, [location.search]);

  // ✅ FIXED: Google Sign In - API returns "token" field
  const handleGoogleSignUp = async (credentialResponse) => {
    setIsLoading(true);
    const decoded = jwtDecode(credentialResponse.credential);
    const { email, sub, name } = decoded;

    try {
      const loginResponse = await axios.post(
        `${config.apiBaseUrl}/api/users/google-signin`,
        { email, gid: sub, name }
      );

      // ✅ API returns { token: "..." } - store as accessToken
      if (loginResponse.status === 200 && loginResponse.data.token) {
        localStorage.setItem("accessToken", loginResponse.data.token);
        
        Toast.fire({
          icon: "success",
          title: loginResponse.data.message,
        });
        navigate("/dashboard");
      } else {
        Toast.fire({
          icon: "error",
          title: "Login successful but no token received",
        });
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401 || error.response.status === 403) {
          Toast.fire({
            icon: "error",
            title: error.response.data.message || "Authentication failed",
          });
        } else if (error.response.status === 500) {
          Toast.fire({
            icon: "error",
            title: "Server error. Please try again later.",
          });
        } else if (error.response.status === 404) {
          Toast.fire({
            icon: "error",
            title: error.response.data.message || "Authentication failed",
          });
          navigate("/signup");
        } else {
          Toast.fire({
            icon: "error",
            title: error.response.data.message || "An error occurred",
          });
        }
      } else if (error.request) {
        Toast.fire({
          icon: "error",
          title: "No response from server. Please check your internet connection.",
        });
      } else {
        Toast.fire({
          icon: "error",
          title: "An unexpected error occurred",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ FIXED: Email/Password Login - API returns "token" field
  const handleLogin = async (event) => {
    event.preventDefault();

    if (!email.trim() || !password.trim()) {
      Toast.fire({
        icon: "error",
        title: "Please enter both email and password",
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/users/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password, mode: "signup" }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.status === 401 || data.status === 403 || data.status === 500) {
          Toast.fire({
            icon: "error",
            title: data.message || "Authentication failed",
          });
        } else {
          Toast.fire({
            icon: "error",
            title: data.message || "An error occurred",
          });
        }
        throw new Error(data.message || "Login failed");
      } else if (response.ok) {
        Toast.fire({
          icon: "success",
          title: "Login Successful",
        });

        // ✅ API returns { token: "..." } - store as accessToken
        localStorage.setItem("accessToken", data.token);
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Login error:", error);
      Toast.fire({
        icon: "error",
        title: error.message || "An unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${config.apiBaseUrl}/forgot`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: forgotemail }),
      });

      const data = await response.json();

      if (response.ok) {
        Toast.fire({
          icon: "success",
          title: data.message || "Check your email to reset your password.",
        });
        setForgotEmail("");
        setShowPopup(false);
      } else {
        Toast.fire({
          icon: "error",
          title: data.message || "Something went wrong. Please try again later.",
        });
      }
    } catch (error) {
      Toast.fire({
        icon: "error",
        title: "Failed to send reset email. Please check your network connection.",
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Login - Viszapp</title>
        <meta
          name="description"
          content="Login to your Viszapp account securely. Access your dashboard and manage your visa applications with ease."
        />
        <meta
          name="keywords"
          content="viszapp login, Viszapp, visa application login, schengen visa, UK visa, US visa"
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Login - Viszapp" />
        <meta
          property="og:description"
          content="Login to your Viszapp account securely."
        />
        <meta property="og:url" content="https://viszapp.com/login" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-[#1C1F2E] via-[#2A3142] to-[#2A3142] flex flex-col">
        <AuthHeader />

        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-0 w-[400px] h-[400px] bg-teal-500/10 rounded-full filter blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-purple-500/10 rounded-full filter blur-[100px] animate-pulse delay-1000"></div>
        </div>

        <div className="relative z-10 flex-1 flex items-center justify-center px-4 py-24 sm:px-6 lg:px-8">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
                Welcome Back
              </h1>
              <p className="text-gray-400 text-base sm:text-lg">
                Login to continue your visa journey
              </p>
            </div>

            <div className="flex items-center justify-center gap-6 mb-8">
              <div className="flex items-center gap-2">
                <FiCheckCircle className="text-teal-400 text-lg" />
                <span className="text-gray-400 text-sm">Secure Login</span>
              </div>
              <div className="flex items-center gap-2">
                <FiCheckCircle className="text-teal-400 text-lg" />
                <span className="text-gray-400 text-sm">2000+ Users</span>
              </div>
            </div>

            <div className="bg-[#2F3651]/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6 sm:p-8 shadow-xl">
              <div className="flex justify-center mb-6">
                <GoogleLogin
                  onSuccess={handleGoogleSignUp}
                  onError={() => {
                    console.log("Google Login Failed");
                  }}
                  uxMode="redirect"
                  redirectUri={`${window.location.origin}/dashboard`}
                />
              </div>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-[#2F3651] text-gray-400">Or continue with email</span>
                </div>
              </div>

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiMail className="text-gray-500" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-[#252A3D] border border-gray-700 rounded-lg
                               text-white placeholder-gray-500 focus:outline-none focus:border-teal-500
                               transition-colors duration-300"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiLock className="text-gray-500" />
                    </div>
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-12 py-3 bg-[#252A3D] border border-gray-700 rounded-lg
                               text-white placeholder-gray-500 focus:outline-none focus:border-teal-500
                               transition-colors duration-300"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500
                               hover:text-gray-400 transition-colors"
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowPopup(true)}
                    className="text-sm text-teal-400 hover:text-teal-300 transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg
                           bg-gradient-to-r from-teal-500 to-teal-400 text-white font-semibold
                           hover:shadow-lg hover:shadow-teal-500/25 transition-all duration-300
                           disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Logging in...</span>
                    </>
                  ) : (
                    <>
                      <span>Login</span>
                      <FiArrowRight />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-gray-400 text-sm">
                  Don't have an account?{" "}
                  <Link to="/signup" className="text-teal-400 hover:text-teal-300 font-medium transition-colors">
                    Sign up
                  </Link>
                </p>
              </div>
            </div>

            <p className="mt-6 text-center text-xs text-gray-500">
              By logging in, you agree to our{" "}
              <Link to="/terms" className="text-gray-400 hover:text-teal-400 transition-colors">
                Terms & Conditions
              </Link>
              , {" "}
              <Link to="/privacy" className="text-gray-400 hover:text-teal-400 transition-colors">
                Privacy Policy
              </Link>
              {" "}and{" "}
              <Link to="/service-policies" className="text-gray-400 hover:text-teal-400 transition-colors">
                Service Policies
              </Link>
            </p>
          </div>
        </div>

        <AuthFooter />

        {showPopup && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[#2F3651] border border-gray-700 rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Reset Password</h2>
                <button
                  onClick={() => setShowPopup(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-gray-400 mb-6">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiMail className="text-gray-500" />
                    </div>
                    <input
                      id="forgot-email"
                      type="email"
                      value={forgotemail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      className="w-full pl-10 pr-4 py-3 bg-[#252A3D] border border-gray-700 rounded-lg
                               text-white placeholder-gray-500 focus:outline-none focus:border-teal-500
                               transition-colors duration-300"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-teal-500 to-teal-400
                           text-white font-semibold hover:shadow-lg hover:shadow-teal-500/25
                           transition-all duration-300"
                >
                  Send Reset Link
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Login;